import { Box, chakra, defineStyle, HStack, VStack, Text, Center } from '@chakra-ui/react';
import {
  CallerInfo,
  CallMainPanelFooter,
  CallTimer,
  DrawSpeak,
  VideoPoorNetIcon,
  VideoErrNetIcon,
} from '@halo/ui-chat-react';
import { useDragMove } from '@halo/hooks';
import { SubscribeStatus, SubscribeType } from '@halo/chat-sdk';
import { useCyclicVisibility } from '@halo/ui-chat-react/hooks';
import { useTranslation } from 'react-i18next';

const slefCard = defineStyle({
  position: 'absolute',
  bottom: '160px',
  right: '28px',
  width: '222px',
  height: '158px',
});
export const PinSidePanel = ({
  bg = '',
  avatar,
  avatarSize = 800,
  actions = true,
  disabled = false,
  isOnly = false,
  member,
  handleDrawSpectrum,
  startTime,
  ondbClick,
  onSubscribe,
  subscribeCollection,
}: {
  bg?: string;
  avatar?: string;
  avatarSize?: number;
  actions?: boolean;
  disabled?: boolean;
  isOnly?: boolean;
  member?: any;
  handleDrawSpectrum?: any;
  startTime?: number;
  ondbClick?: (member: Record<string, any>) => void;
  onSubscribe?: (member: any, status: SubscribeStatus) => void;
  subscribeCollection?: (members: Record<string, any>[], type: SubscribeType) => Promise<any>;
}) => {
  const [audioInfo, setAudioInfo] = useState({ hasAudioStream: false, hasVolume: false });
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { dragRef, style } = useDragMove({
    disabled,
    marginBottom: 76,
    thresholdBottom: 76,
    showLabelLeft: false,
    showLabelRight: false,
  });

  const { show, checkShow } = useCyclicVisibility();

  const netInfo = useMemo(() => {
    const info = { isPoorNet: false, isErrNet: false };
    if (member?.network?.ingress && member?.network?.egress) {
      info.isPoorNet = Math.floor((member?.network?.ingress + member?.network?.egress) / 2) <= 1;
    } else if (member?.network?.ingress) {
      info.isPoorNet = member?.network?.ingress <= 1;
    } else if (member?.network?.egress) {
      info.isPoorNet = member?.network?.egress <= 1;
    } else {
      info.isPoorNet = true;
      info.isErrNet = true;
    }
    return info;
  }, [member?.network?.egress, member?.network?.ingress]);

  const stream = useMemo<MediaStream | undefined>(() => {
    if (member?.screenSharing) {
      return member?.streams?.screenSharing_video?.enabled
        ? member?.streams?.screenSharing_video?.stream
        : undefined;
    } else {
      return member?.streams?.microphoneCamera_video?.enabled
        ? member?.streams?.microphoneCamera_video?.stream
        : undefined;
    }
  }, [
    member?.streams?.microphoneCamera_video,
    member?.streams?.screenSharing_video,
    member?.screenSharing,
  ]);

  const showMinVoice = useMemo(() => {
    return disabled && !member?.isSelf && !member?.screenSharing && !stream;
  }, [disabled, member?.isSelf, member?.screenSharing, stream]);

  useEffect(() => {
    const dom = videoRef.current;
    if (dom) {
      if (stream) {
        dom.srcObject = stream;
        dom.load();
        dom.play();
      }
    }
  }, [
    stream,
    member?.callActionMap?.microphoneCamera_video,
    member?.callActionMap?.screenSharing_video,
  ]);

  useEffect(() => {
    (async () => {
      if (
        (member?.streams?.screenSharing_video?.enabled ||
          member?.streams?.microphoneCamera_video?.enabled) &&
        subscribeCollection &&
        onSubscribe &&
        !member?.isSelf
      ) {
        onSubscribe(member, SubscribeStatus.SUBSCRIBEING);
        const subscribeInfo = await subscribeCollection([member], SubscribeType.SUBSCRIBE)?.catch(
          () => {
            onSubscribe(member, SubscribeStatus.SUBSCRIBE_FAIL);
          }
        );
        if (subscribeInfo === true) {
          onSubscribe(member, SubscribeStatus.SUBSCRIBE_SUCCESS);
        } else {
          onSubscribe(member, SubscribeStatus.SUBSCRIBE_FAIL);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    member?.streams?.microphoneCamera_video?.enabled,
    member?.streams?.screenSharing_video?.enabled,
    onSubscribe,
    subscribeCollection,
  ]);

  useEffect(() => {
    checkShow(!netInfo.isPoorNet);
  }, [checkShow, netInfo.isPoorNet]);

  return (
    <Box
      ref={dragRef}
      rounded="lg"
      border={
        (!member?.isSelf || member?.screenSharing || isOnly) && disabled
          ? 'none'
          : '1px solid #CCC'
      }
      bg={avatar ? 'lightgray' : `linear-gradient( rgba(0,0,0,0.7))${bg ? `, ${bg}` : ''}`}
      _before={
        avatar
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              bgImage: `url(${avatar})`,
              bgSize: `${avatarSize}px ${avatarSize}px`,
              bgPos: 'center',
              bgRepeat: 'no-repeat',
              filter: `blur(${avatarSize / 10}px)`,
              zIndex: -1,
            }
          : undefined
      }
      display="flex"
      alignItems={showMinVoice ? 'initial' : 'center'}
      pt={showMinVoice ? '42px' : '0'}
      justifyContent="center"
      css={disabled ? { w: 'full', h: 'full', ...style } : { ...slefCard, ...style }}
      onDoubleClick={() => ondbClick?.(member)}
      overflow="hidden"
      zIndex={1}
    >
      {!stream ||
      (member?.screenSharing
        ? !member?.callActionMap?.screenSharing_video
        : !member?.callActionMap?.microphoneCamera_video) ? (
        <VStack>
          <CallerInfo
            avatar={member?.avatar}
            name={member?.isSelf ? t('calls.me') : member?.name}
            isConnect={true}
            size={(disabled || member?.isSelf) && !isOnly ? 'lg' : '3xl'}
            showBg={audioInfo.hasAudioStream}
            hasVolume={audioInfo.hasVolume}
          />
          {showMinVoice && (
            <HStack>
              <DrawSpeak
                handleDrawSpectrum={handleDrawSpectrum}
                member={member}
                onAudioInfo={(info) => setAudioInfo(info)}
              ></DrawSpeak>
              <CallTimer startTime={startTime}></CallTimer>
            </HStack>
          )}
        </VStack>
      ) : (
        <Box w="full" h="full">
          <chakra.video
            w="full"
            h="full"
            objectFit="contain"
            playsInline
            autoPlay
            muted
            loop
            ref={videoRef}
          ></chakra.video>
        </Box>
      )}

      {actions && (
        <Box pos="absolute" bottom="0" width="full">
          {netInfo.isPoorNet && show && (
            <Center gap="1">
              {netInfo.isErrNet ? <VideoErrNetIcon /> : <VideoPoorNetIcon />}
              <Text color="#FF0015" fontSize="sm">
                {t('calls.poorNet')}
              </Text>
            </Center>
          )}
          <CallMainPanelFooter
            disabled={!member?.isSelf}
            member={member}
            handleDrawSpectrum={handleDrawSpectrum}
            onAudioInfo={(info) => setAudioInfo(info)}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(PinSidePanel);
